import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow } from "@/components/ui-bits";
import api, { formatApiErrorDetail } from "@/lib/api";
import { AlertTriangle, ArrowRight, CheckCircle2, XCircle, Info, ExternalLink, RotateCcw, Search, Copy, Check } from "lucide-react";

// Multi-step EAR License Requirement Checker. Flow:
//   1. User picks ECCN + destination country
//   2. If country has sanctioned regions → REGION_PROMPT (pick region)
//   3. If country/region is embargoed → EMBARGO (hard stop)
//   4. If ECCN has paragraph-scoped rules → FOLLOW_UP (checkbox paragraphs)
//   5. If ECCN has conditional rules ("when usable in missiles") → FOLLOW_UP
//   6. DONE — matched rules table + verdict + special-review flags

const REASON_LABEL = {
  NS: "National Security", RS: "Regional Stability", MT: "Missile Technology",
  NP: "Nuclear Nonproliferation", CB: "Chemical & Biological Weapons",
  CC: "Crime Control", AT: "Anti-Terrorism", FC: "Firearms Convention",
  EI: "Encryption Items", UN: "United Nations Embargo",
  SL: "Surreptitious Listening", SI: "Significant Items", SS: "Short Supply",
};

// Build a plain-text compliance memo the user can paste into an internal
// record, deal file, or transaction memo. Includes ECCN, destination, the
// full chain of user answers, matched Reasons for Control, special-review
// items, and the standard caveats. Ends with attribution + disclaimer.
function buildMemo(result, ctx) {
  const lines = [];
  const today = new Date().toISOString().slice(0, 10);
  lines.push("EAR License Requirement — Preliminary Determination");
  lines.push("=".repeat(56));
  lines.push(`ECCN:          ${result.eccn}`);
  lines.push(`Destination:   ${result.country_label || (result.country_slug || "").replace(/-/g, " ").replace(/\b\w/g, s => s.toUpperCase())}`);
  if (result.region_slug || ctx.region_label) {
    lines.push(`Region:        ${ctx.region_label || result.region_slug}`);
  }
  if (ctx.selected_paragraphs?.length) {
    lines.push(`Paragraphs:    ${ctx.selected_paragraphs.map((p) => "." + p).join(", ")}`);
  }
  if (ctx.answered_conditionals?.length) {
    lines.push("Answers to conditional rules:");
    for (const a of ctx.answered_conditionals) {
      lines.push(`  - ${a.reason}: "${a.condition}" — ${a.answer ? "YES" : "NO"}`);
    }
  }
  lines.push(`Date:          ${today}`);
  lines.push("");
  lines.push("VERDICT");
  lines.push("-".repeat(56));
  if (result.status === "EMBARGO") {
    lines.push("License required — comprehensive embargo.");
    lines.push(result.reason || "");
  } else if (result.license_required) {
    lines.push("License required under the Commerce Country Chart.");
  } else {
    lines.push("No license required under the Commerce Country Chart.");
  }
  lines.push("");

  if (result.matched_rules?.length) {
    lines.push("MATCHED REASONS FOR CONTROL");
    lines.push("-".repeat(56));
    for (const r of result.matched_rules) {
      const verdict = r.x_mark ? "X — license required for this reason" : "no X — no license required for this reason";
      lines.push(`- ${r.reason} → ${r.column} — ${verdict}`);
      lines.push(`  Basis: ${r.raw_control}`);
      if (r.note) lines.push(`  Note: ${r.note}`);
    }
    lines.push("");
  }

  if (result.special_review?.length) {
    lines.push("ADDITIONAL REVIEW NEEDED (outside the Country Chart)");
    lines.push("-".repeat(56));
    for (const s of result.special_review) {
      lines.push(`- ${s.reason}: ${s.raw_control}`);
      lines.push(`  Chart cell: ${s.raw_chart}`);
    }
    lines.push("");
  }

  lines.push("OTHER REASONS A LICENSE MAY BE REQUIRED");
  lines.push("-".repeat(56));
  lines.push("The Commerce Country Chart is only one of several EAR license triggers.");
  lines.push("This determination does NOT account for:");
  lines.push("  - End users (Entity List, MEU List, Denied Persons — 15 CFR §§ 744, 764)");
  lines.push("  - End uses (military, military-intelligence, WMD, missile, nuclear — 15 CFR §§ 744.2–744.11, 744.17, 744.21)");
  lines.push("  - U.S.-person \"support\" activities (15 CFR § 744.6)");
  lines.push("  - Deemed exports (15 CFR § 734.13(a)(2))");
  lines.push("  - OFAC sanctions (31 CFR Chapter V)");
  lines.push("  - License Exception eligibility (15 CFR Part 740)");
  lines.push("");
  lines.push("Generated by Shelton Law's EAR License Checker — https://shelton.law/tools/license-check");
  lines.push("Preliminary review only. Not legal advice. Consult counsel before making an export decision.");
  return lines.join("\n");
}

export default function LicenseCheck() {
  const [eccnList, setEccnList] = useState([]);
  const [countries, setCountries] = useState([]);
  const [regioned, setRegioned] = useState({});
  const [eccn, setEccn] = useState("");
  const [countrySlug, setCountrySlug] = useState("");
  const [regionSlug, setRegionSlug] = useState(null);
  const [selectedParagraphs, setSelectedParagraphs] = useState([]);
  const [confirmedConds, setConfirmedConds] = useState([]);
  const [deniedConds, setDeniedConds] = useState([]);
  const [answerLog, setAnswerLog] = useState([]);   // [{reason, condition, answer}]
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [eccnQuery, setEccnQuery] = useState("");
  const [eccnFocused, setEccnFocused] = useState(false);
  const eccnBoxRef = useRef(null);

  // Load ECCN + country lists on mount.
  useEffect(() => {
    (async () => {
      try {
        const [e, c] = await Promise.all([
          api.get("/ccl/entries", { params: { limit: 700 } }),
          api.get("/tools/license-check/countries"),
        ]);
        setEccnList(e.data.entries || []);
        setCountries(c.data.countries || []);
        setRegioned(c.data.regioned || {});
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load reference data.");
      }
    })();
  }, []);

  // Close the ECCN suggestions dropdown when clicking outside.
  useEffect(() => {
    function onClick(e) {
      if (eccnBoxRef.current && !eccnBoxRef.current.contains(e.target)) setEccnFocused(false);
    }
    document.addEventListener("pointerdown", onClick);
    return () => document.removeEventListener("pointerdown", onClick);
  }, []);

  const eccnSuggestions = useMemo(() => {
    if (!eccnQuery.trim()) return eccnList.slice(0, 20);
    const q = eccnQuery.trim().toLowerCase();
    return eccnList.filter((e) =>
      e.eccn.toLowerCase().includes(q) || (e.heading || "").toLowerCase().includes(q)
    ).slice(0, 20);
  }, [eccnList, eccnQuery]);

  const check = async (overrides = {}) => {
    setLoading(true);
    setError("");
    try {
      const body = {
        eccn: overrides.eccn ?? eccn,
        country_slug: overrides.country_slug ?? countrySlug,
        region_slug: overrides.region_slug !== undefined ? overrides.region_slug : regionSlug,
        selected_paragraphs: overrides.selected_paragraphs ?? selectedParagraphs,
        confirmed_conditionals: overrides.confirmed_conditionals ?? confirmedConds,
        denied_conditionals: overrides.denied_conditionals ?? deniedConds,
      };
      const r = await api.post("/tools/license-check", body);
      setResult(r.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Check failed.");
    } finally { setLoading(false); }
  };

  const reset = () => {
    setResult(null);
    setRegionSlug(null);
    setSelectedParagraphs([]);
    setConfirmedConds([]);
    setDeniedConds([]);
    setAnswerLog([]);
    setError("");
  };

  const chosenCountry = countries.find((c) => c.slug === countrySlug);
  const chosenRegionLabel = useMemo(() => {
    if (!regionSlug) return null;
    const list = regioned[countrySlug] || [];
    const r = list.find((x) => x.slug === regionSlug);
    return r?.label || regionSlug;
  }, [regioned, countrySlug, regionSlug]);

  return (
    <Layout>
      <SEO
        title="EAR License Requirement Checker — Shelton Law"
        description="Enter an ECCN and destination country to see whether an export license is required under the Commerce Country Chart (15 CFR Part 738)."
        path="/tools/license-check"
      />

      {/* Hero */}
      <section className="border-b border-hairline">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 pt-14 pb-10">
          <Eyebrow>Compliance Tool</Eyebrow>
          <h1 className="font-serif text-4xl sm:text-5xl text-ink leading-[1.05] tracking-tight mt-4">
            EAR License Requirement Checker
          </h1>
          <p className="mt-6 text-ink-soft max-w-3xl leading-relaxed text-[15px]">
            Pick an ECCN and a destination. The tool matches each Reason for Control against the Commerce Country Chart (Supplement No. 1 to Part 738) and returns whether an export license would be required under the CCL. Comprehensive embargoes (Cuba, Iran, North Korea, Syria, plus sanctioned regions of Ukraine) return a hard stop.
          </p>
        </div>
      </section>

      {/* Disclaimer strip */}
      <div className="bg-brass-deep/10 border-b border-brass/40" data-testid="license-check-disclaimer">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-3 flex items-start gap-3 text-[12px] leading-snug">
          <AlertTriangle className="w-4 h-4 text-brass-deep shrink-0 mt-0.5" strokeWidth={2} />
          <p className="text-ink">
            <strong className="uppercase tracking-wide text-[10px]">Not legal advice.</strong>{" "}
            This tool checks the Country Chart only. A License Exception may still apply, and end-use, end-user, and sanctions rules may impose their own requirements. Verify every result against the live eCFR and consult counsel before making an export decision.
          </p>
        </div>
      </div>

      {/* Form / Result */}
      <section className="max-w-4xl mx-auto px-6 lg:px-8 py-10">
        {!result && (
          <form
            onSubmit={(e) => { e.preventDefault(); if (eccn && countrySlug) check(); }}
            className="space-y-8"
            data-testid="license-check-form"
          >
            {/* ECCN */}
            <div ref={eccnBoxRef} className="relative">
              <label className="block text-[11px] uppercase tracking-[0.22em] text-ink-soft font-semibold mb-2">ECCN</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" strokeWidth={2} />
                <input
                  value={eccn || eccnQuery}
                  onFocus={() => { setEccnFocused(true); if (eccn) setEccnQuery(eccn); setEccn(""); }}
                  onChange={(e) => { setEccnQuery(e.target.value.toUpperCase()); setEccn(""); }}
                  placeholder="e.g. 5A002, 3A001, 9A515"
                  className="w-full pl-10 pr-3 py-3 bg-ivory border border-hairline text-[15px] text-ink placeholder:text-ink-soft/70 focus:outline-none focus:border-brass font-mono"
                  data-testid="license-check-eccn-input"
                />
              </div>
              {eccnFocused && eccnSuggestions.length > 0 && (
                <ul className="absolute z-20 left-0 right-0 mt-1 bg-ivory border border-hairline shadow-lg max-h-72 overflow-y-auto">
                  {eccnSuggestions.map((e) => (
                    <li key={e.eccn}>
                      <button
                        type="button"
                        onClick={() => { setEccn(e.eccn); setEccnQuery(""); setEccnFocused(false); }}
                        className="w-full text-left px-4 py-2.5 hover:bg-ivory-alt transition-colors border-b border-hairline-soft"
                        data-testid={`license-check-suggestion-${e.eccn}`}
                      >
                        <span className="font-mono text-[13px] font-semibold text-brass-deep">{e.eccn}</span>
                        <span className="ml-3 text-[13px] text-ink-soft truncate">{e.heading}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Country */}
            <div>
              <label className="block text-[11px] uppercase tracking-[0.22em] text-ink-soft font-semibold mb-2">Destination country</label>
              <select
                value={countrySlug}
                onChange={(e) => setCountrySlug(e.target.value)}
                className="w-full px-3 py-3 bg-ivory border border-hairline text-[15px] text-ink focus:outline-none focus:border-brass"
                data-testid="license-check-country-select"
              >
                <option value="">— Select a destination —</option>
                {countries.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.country}{c.embargoed ? " (embargoed)" : ""}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              disabled={!eccn || !countrySlug || loading}
              className="inline-flex items-center gap-2 bg-ink text-ivory px-8 py-3 text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-brass transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="license-check-submit"
            >
              {loading ? "Checking…" : "Check license requirement"} <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </button>
            {error && <p className="text-red-700 text-sm" data-testid="license-check-error">{error}</p>}
          </form>
        )}

        {result && (
          <ResultCard
            result={result}
            eccn={eccn}
            chosenCountry={chosenCountry}
            memoCtx={{
              selected_paragraphs: selectedParagraphs,
              answered_conditionals: answerLog,
              region_label: chosenRegionLabel,
            }}
            onPickRegion={(slug) => { setRegionSlug(slug); check({ region_slug: slug }); }}
            onSubmitParagraphs={(paras) => { setSelectedParagraphs(paras); check({ selected_paragraphs: paras }); }}
            onAnswerConditional={(key, answer, meta) => {
              setAnswerLog((prev) => [...prev, { ...meta, answer }]);
              if (answer) {
                const next = [...confirmedConds, key];
                setConfirmedConds(next);
                check({ confirmed_conditionals: next });
              } else {
                const next = [...deniedConds, key];
                setDeniedConds(next);
                check({ denied_conditionals: next });
              }
            }}
            onReset={reset}
          />
        )}
      </section>
    </Layout>
  );
}

function ResultCard({ result, eccn, chosenCountry, memoCtx, onPickRegion, onSubmitParagraphs, onAnswerConditional, onReset }) {
  const [pickedParas, setPickedParas] = useState([]);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      const text = buildMemo(result, memoCtx || {});
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  };

  const copyBtn = (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-2 bg-brass-deep text-ivory px-6 py-2.5 text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-brass transition-colors"
      data-testid="license-check-copy"
    >
      {copied ? <><Check className="w-4 h-4" strokeWidth={2} /> Copied</> : <><Copy className="w-4 h-4" strokeWidth={2} /> Copy summary</>}
    </button>
  );

  const restartBtn = (
    <button
      type="button"
      onClick={onReset}
      className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-ink-soft hover:text-brass transition-colors mt-8"
      data-testid="license-check-reset"
    >
      <RotateCcw className="w-3.5 h-3.5" strokeWidth={2} /> Start over
    </button>
  );

  // 1) Region prompt (Ukraine)
  if (result.status === "REGION_PROMPT") {
    return (
      <div data-testid="license-check-region-prompt">
        <SectionHeading eyebrow="Follow-up" title={`Which region of ${chosenCountry?.country}?`} />
        <p className="text-ink-soft mb-6">Some regions of {chosenCountry?.country} are subject to comprehensive sanctions. Please pick the destination region.</p>
        <div className="space-y-2">
          {result.regions.map((r) => (
            <button
              key={r.slug}
              type="button"
              onClick={() => onPickRegion(r.slug)}
              className="w-full text-left border border-hairline px-4 py-3 hover:border-brass hover:bg-ivory-alt transition-colors flex items-center justify-between"
              data-testid={`license-check-region-${r.slug}`}
            >
              <span className="text-ink">{r.label}</span>
              {r.embargoed && <span className="text-[10px] uppercase tracking-[0.22em] text-red-800 font-semibold">Embargoed</span>}
            </button>
          ))}
        </div>
        {restartBtn}
      </div>
    );
  }

  // 2) Embargo hard stop
  if (result.status === "EMBARGO") {
    return (
      <div data-testid="license-check-embargo">
        <SectionHeading eyebrow="Result" title="License required — comprehensive embargo" tone="red" />
        <div className="border-l-4 border-red-800 bg-red-50 p-6 mb-6">
          <p className="text-ink leading-relaxed">{result.reason}</p>
          <p className="text-ink-soft text-sm mt-3">Exports and reexports to this destination require a specific BIS license (and generally an OFAC license as well) — regardless of whether the ECCN itself imposes controls. Most items are subject to policy of denial.</p>
        </div>
        <p className="text-ink-soft text-sm">
          Reference: <a href="https://www.ecfr.gov/current/title-15/subtitle-B/chapter-VII/subchapter-C/part-746" target="_blank" rel="noopener noreferrer" className="underline text-brass-deep">15 CFR Part 746 (Embargoes and Other Special Controls) <ExternalLink className="w-3 h-3 inline" /></a>
        </p>
        <div className="mt-6">{copyBtn}</div>
        {restartBtn}
      </div>
    );
  }

  // 3) Follow-up: paragraphs
  if (result.status === "FOLLOW_UP" && result.prompt_paragraphs) {
    return (
      <div data-testid="license-check-follow-up-paragraphs">
        <SectionHeading eyebrow="Follow-up" title={`Which paragraph(s) of ${result.eccn} apply to your item?`} />
        <p className="text-ink-soft mb-6">Some Reasons for Control apply only to specific paragraphs of this ECCN. Check every top-level paragraph your item falls under.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-8">
          {result.prompt_paragraphs.map((p) => {
            const checked = pickedParas.includes(p);
            return (
              <label
                key={p}
                className={`cursor-pointer border px-4 py-3 flex items-center gap-2 transition-colors ${
                  checked ? "border-brass bg-ivory-alt text-brass-deep" : "border-hairline hover:border-brass"
                }`}
                data-testid={`license-check-para-${p}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => setPickedParas((prev) => checked ? prev.filter((x) => x !== p) : [...prev, p])}
                  className="accent-brass"
                />
                <span className="font-mono font-semibold">.{p}</span>
              </label>
            );
          })}
        </div>
        <button
          type="button"
          disabled={pickedParas.length === 0}
          onClick={() => onSubmitParagraphs(pickedParas)}
          className="inline-flex items-center gap-2 bg-ink text-ivory px-8 py-3 text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-brass transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="license-check-para-submit"
        >
          Continue <ArrowRight className="w-4 h-4" strokeWidth={2} />
        </button>
        {restartBtn}
      </div>
    );
  }

  // 4) Follow-up: conditionals
  if (result.status === "FOLLOW_UP" && result.prompt_conditionals?.length) {
    const c = result.prompt_conditionals[0];
    return (
      <div data-testid="license-check-follow-up-conditional">
        <SectionHeading eyebrow="Follow-up" title="Does this describe your item?" />
        <div className="border-l-4 border-brass bg-ivory-alt p-6 mb-6">
          <p className="text-[10px] uppercase tracking-[0.22em] text-brass-deep font-semibold mb-2">Reason: {c.reason} ({REASON_LABEL[c.reason] || c.reason})</p>
          <p className="font-serif text-xl text-ink leading-snug">{c.raw_control}</p>
        </div>
        <p className="text-ink-soft mb-6">Answering &ldquo;yes&rdquo; means this Reason for Control applies to your item. Answering &ldquo;no&rdquo; means the rule does not apply.</p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => onAnswerConditional(c.key, true, { reason: c.reason, condition: c.condition, raw_control: c.raw_control })}
            className="bg-ink text-ivory px-8 py-3 text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-brass transition-colors"
            data-testid="license-check-cond-yes"
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => onAnswerConditional(c.key, false, { reason: c.reason, condition: c.condition, raw_control: c.raw_control })}
            className="border border-ink text-ink px-8 py-3 text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-ivory-alt transition-colors"
            data-testid="license-check-cond-no"
          >
            No
          </button>
        </div>
        {restartBtn}
      </div>
    );
  }

  // 5) DONE
  const required = !!result.license_required;
  return (
    <div data-testid="license-check-done">
      <SectionHeading
        eyebrow="Result"
        title={required ? "License required" : "No license required under the Country Chart"}
        tone={required ? "red" : "green"}
      />
      <div className={`border-l-4 p-6 mb-8 ${required ? "border-red-800 bg-red-50" : "border-emerald-700 bg-emerald-50"}`}>
        <p className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-3 text-ink-soft">
          {result.eccn} → {result.country_label}
        </p>
        <p className="font-serif text-2xl text-ink leading-snug">
          {required
            ? `An export license is required from BIS for this shipment.`
            : `Based on the CCL Reasons for Control × Country Chart, no license is required for this destination.`}
        </p>
      </div>

      {/* Broad "other reasons a license may still be needed" reminder — shown
          on every final result. This tool only checks Reasons for Control
          against the Country Chart; the EAR imposes several parallel
          obligations that no CCL/CCC lookup can resolve. */}
      <div className="border-l-4 border-brass-deep bg-brass-deep/5 p-6 mb-8" data-testid="license-check-other-caveats">
        <p className="text-[10px] uppercase tracking-[0.28em] text-brass-deep font-semibold mb-3">
          Other reasons a license may {required ? "also " : ""}be required
        </p>
        <p className="text-ink leading-relaxed text-[14px]">
          The Country Chart is only one of several EAR license triggers. Even
          {required ? " if" : " when the Chart shows no controls,"} a license (or
          license exception denial) may still be required based on:
        </p>
        <ul className="mt-3 space-y-1.5 text-[13px] text-ink leading-relaxed">
          <li>&bull; <strong>End users</strong> — Entity List, Military End User (MEU) List, Denied Persons, Unverified List, or Debarred parties (15 CFR §§ 744, 764)</li>
          <li>&bull; <strong>End uses</strong> — military, military-intelligence, WMD, missile, nuclear, or maritime nuclear propulsion (15 CFR § 744.2–744.11, § 744.17, § 744.21)</li>
          <li>&bull; <strong>U.S.-person &ldquo;support&rdquo; activities</strong> — servicing, brokering, financing, or shipping certain foreign-produced items, even when the underlying goods aren&apos;t U.S.-origin (15 CFR § 744.6)</li>
          <li>&bull; <strong>Deemed exports</strong> — release of controlled technology or source code to a foreign national inside the United States (15 CFR § 734.13(a)(2))</li>
          <li>&bull; <strong>OFAC sanctions</strong> — country-, region-, or SDN-based prohibitions that operate independently of the EAR (31 CFR Chapter V)</li>
          <li>&bull; <strong>License Exception eligibility</strong> — {required ? "even if you find an exception that appears to fit, item-, destination-, or end-use-based carve-outs may disqualify it" : "although no license is required here, an exception may still be needed to properly document the transaction"} (15 CFR Part 740)</li>
        </ul>
        <p className="text-ink-soft text-[12px] leading-relaxed mt-4 italic">
          This tool does not evaluate any of the above. Consult counsel before making an export decision.
        </p>
      </div>

      {/* Matched rules */}
      {result.matched_rules?.length > 0 && (
        <div className="mb-10">
          <p className="text-[10px] uppercase tracking-[0.28em] text-brass-deep font-semibold mb-4">Matched Reasons for Control</p>
          <ul className="border-t border-hairline">
            {result.matched_rules.map((r, i) => (
              <li key={i} className="border-b border-hairline py-4 flex items-start gap-4" data-testid={`license-check-matched-${r.reason}-${i}`}>
                {r.x_mark
                  ? <XCircle className="w-5 h-5 text-red-700 shrink-0 mt-1" strokeWidth={2} />
                  : <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-1" strokeWidth={2} />
                }
                <div className="flex-1">
                  <p className="font-mono text-[13px] font-semibold text-ink">
                    {r.reason} → <span className="text-brass-deep">{r.column}</span> —{" "}
                    <span className={r.x_mark ? "text-red-700" : "text-emerald-700"}>
                      {r.x_mark ? "X in Country Chart (license required for this reason)" : "no X in Country Chart"}
                    </span>
                  </p>
                  <p className="text-[13px] text-ink-soft mt-1 leading-snug">{r.raw_control}</p>
                  {r.note && <p className="text-[12px] text-ink-soft/80 italic mt-1">{r.note}</p>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Special review */}
      {result.special_review?.length > 0 && (
        <div className="mb-10">
          <p className="text-[10px] uppercase tracking-[0.28em] text-brass-deep font-semibold mb-4">Additional review needed (outside the Country Chart)</p>
          <ul className="border-t border-hairline">
            {result.special_review.map((s, i) => (
              <li key={i} className="border-b border-hairline py-4 flex items-start gap-4" data-testid={`license-check-special-${i}`}>
                <Info className="w-5 h-5 text-brass-deep shrink-0 mt-1" strokeWidth={2} />
                <div className="flex-1">
                  <p className="font-mono text-[13px] font-semibold text-ink">{s.reason}</p>
                  <p className="text-[13px] text-ink leading-snug">{s.raw_control}</p>
                  <p className="text-[12px] text-ink-soft italic mt-1">Chart cell: {s.raw_chart}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Cross-links */}
      <div className="flex flex-wrap gap-3 items-center">
        {copyBtn}
        <Link to={`/ccl/e/${result.eccn}`} className="border-b border-brass text-ink hover:text-brass transition-colors text-[12px] uppercase tracking-[0.22em] px-2 py-1" data-testid="license-check-view-eccn">
          Open {result.eccn} in CCL Navigator
        </Link>
        <a
          href="https://www.ecfr.gov/current/title-15/subtitle-B/chapter-VII/subchapter-C/part-738#Supplement-No.-1-to-Part-738"
          target="_blank"
          rel="noopener noreferrer"
          className="border-b border-hairline text-ink-soft hover:text-brass hover:border-brass transition-colors text-[12px] uppercase tracking-[0.22em] px-2 py-1"
          data-testid="license-check-view-ccc"
        >
          View Country Chart on eCFR <ExternalLink className="w-3 h-3 inline" strokeWidth={2} />
        </a>
      </div>

      {restartBtn}
    </div>
  );
}

function SectionHeading({ eyebrow, title, tone }) {
  const toneClass = tone === "red" ? "text-red-800" : tone === "green" ? "text-emerald-800" : "text-ink";
  return (
    <div className="mb-6">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className={`font-serif text-3xl sm:text-4xl leading-tight tracking-tight mt-3 ${toneClass}`}>{title}</h2>
    </div>
  );
}
